import { useState, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CalendarDays,
  ArrowUpRight,
  Clock,
  Search,
  Tag,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  BookOpen,
} from "lucide-react";
import { useSiteSection } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { postSlug, estimateReadingTime } from "@/lib/blog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog Contato — Dicas de Wi-Fi, Tecnologia e Novidades" },
      {
        name: "description",
        content:
          "Artigos sobre tecnologia, dicas de conexão Wi-Fi, lançamentos de streaming e novidades da Contato Internet no Sul de Santa Catarina.",
      },
      { property: "og:title", content: "Blog Contato Internet" },
      {
        property: "og:description",
        content: "Dicas de tecnologia, conectividade, streaming e novidades da sua região.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: BlogPage,
});

const POSTS_PER_PAGE = 6;

export function BlogPage() {
  const blog = useSiteSection("blog");
  const allPosts = useMemo(
    () => (blog.items ?? []).filter((p) => p.title && p.title.trim() !== ""),
    [blog.items],
  );

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("Todas");
  const [currentPage, setCurrentPage] = useState(1);

  // Extrai todas as categorias únicas existentes
  const categories = useMemo(() => {
    const set = new Set<string>();
    allPosts.forEach((p) => {
      if (p.category && p.category.trim()) {
        set.add(p.category.trim());
      }
    });
    return ["Todas", ...Array.from(set)];
  }, [allPosts]);

  // Filtra por busca e categoria
  const filteredPosts = useMemo(() => {
    return allPosts.filter((post) => {
      const matchesCat =
        selectedCategory === "Todas" ||
        (post.category && post.category.trim().toLowerCase() === selectedCategory.toLowerCase());

      const query = search.trim().toLowerCase();
      const matchesSearch =
        !query ||
        post.title.toLowerCase().includes(query) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(query)) ||
        (post.category && post.category.toLowerCase().includes(query)) ||
        (post.author && post.author.toLowerCase().includes(query));

      return matchesCat && matchesSearch;
    });
  }, [allPosts, selectedCategory, search]);

  // Post em destaque (apenas na página 1 e sem filtro de busca ou categoria)
  const isDefaultView = selectedCategory === "Todas" && search.trim() === "" && currentPage === 1;
  const featuredPost = useMemo(() => {
    if (!isDefaultView) return null;
    const explicitlyFeatured = allPosts.find((p) => p.featured);
    return explicitlyFeatured || allPosts[0] || null;
  }, [allPosts, isDefaultView]);

  // Posts que entram no grid
  const availableForGrid = useMemo(() => {
    if (featuredPost && isDefaultView) {
      return filteredPosts.filter((p) => postSlug(p) !== postSlug(featuredPost));
    }
    return filteredPosts;
  }, [filteredPosts, featuredPost, isDefaultView]);

  // Paginação dos posts do grid
  const totalPages = Math.max(1, Math.ceil(availableForGrid.length / POSTS_PER_PAGE));
  const validPage = Math.min(currentPage, totalPages);
  const paginatedGridPosts = useMemo(() => {
    const start = (validPage - 1) * POSTS_PER_PAGE;
    return availableForGrid.slice(start, start + POSTS_PER_PAGE);
  }, [availableForGrid, validPage]);

  function handleCategoryChange(cat: string) {
    setSelectedCategory(cat);
    setCurrentPage(1);
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    setCurrentPage(1);
  }

  return (
    <>
      {/* Hero do Blog */}
      <section className="relative overflow-hidden bg-[linear-gradient(115deg,var(--brand-sky-deep),var(--brand-deep))] py-14 text-brand-deep-foreground sm:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1 text-xs font-extrabold uppercase tracking-widest text-brand-yellow backdrop-blur-sm">
            <Sparkles className="size-3.5" /> Blog & Conectividade
          </div>
          <h1 className="mt-4 text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-5xl">
            {blog.title || "Blog Contato"}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/80 sm:text-lg">
            {blog.subtitle ||
              "Dicas de internet, tecnologia, lançamentos e tudo sobre o universo da conexão digital."}
          </p>

          {/* Barra de Busca rápida no Hero */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por artigos, dicas de Wi-Fi, tecnologia..."
                value={search}
                onChange={handleSearchChange}
                className="h-13 rounded-2xl border-white/20 bg-background/95 pl-12 pr-4 text-sm text-foreground shadow-xl backdrop-blur placeholder:text-muted-foreground focus-visible:ring-primary"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-5">
          {/* Barra de Categorias */}
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-2 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
                <Tag className="size-3.5" /> Categorias:
              </span>
              {categories.map((cat) => {
                const active = selectedCategory.toLowerCase() === cat.toLowerCase();
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategoryChange(cat)}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition ${
                      active
                        ? "bg-primary text-primary-foreground shadow"
                        : "border border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            <div className="text-xs font-semibold text-muted-foreground">
              Mostrando {availableForGrid.length + (featuredPost ? 1 : 0)} artigos
            </div>
          </div>

          {/* Post em Destaque (apenas na página 1 sem filtros) */}
          {featuredPost && (
            <div className="mb-14">
              <p className="mb-4 text-xs font-extrabold uppercase tracking-widest text-primary">
                ★ Artigo em Destaque
              </p>
              <article className="group relative grid gap-8 overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition hover:shadow-xl lg:grid-cols-12">
                <div className="lg:col-span-7">
                  {siteImageUrl(featuredPost.image) ? (
                    <img
                      src={siteImageUrl(featuredPost.image)!}
                      alt={featuredPost.title}
                      className="h-64 w-full object-cover transition duration-500 group-hover:scale-105 sm:h-96"
                    />
                  ) : (
                    <div className="flex h-64 w-full items-center justify-center bg-gradient-to-br from-primary/20 via-primary/10 to-card sm:h-96">
                      <BookOpen className="size-16 text-primary/40" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-10 lg:col-span-5">
                  <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-muted-foreground">
                    {featuredPost.category && (
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                        {featuredPost.category}
                      </span>
                    )}
                    {featuredPost.date && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="size-3.5" />
                        {featuredPost.date}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="size-3.5" />
                      {featuredPost.readTime ||
                        estimateReadingTime(featuredPost.body || featuredPost.excerpt)}
                    </span>
                  </div>

                  <h2 className="mt-4 text-2xl font-extrabold leading-tight text-foreground transition group-hover:text-primary sm:text-3xl">
                    {featuredPost.title}
                  </h2>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {featuredPost.excerpt}
                  </p>

                  <div className="mt-6 pt-4">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: postSlug(featuredPost) }}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-extrabold text-primary-foreground shadow transition hover:opacity-90"
                    >
                      Ler artigo completo
                      <ChevronRight className="size-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          )}

          {/* Grid de Artigos */}
          {paginatedGridPosts.length > 0 ? (
            <>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {paginatedGridPosts.map((post) => {
                  const image = siteImageUrl(post.image);
                  const readTime =
                    post.readTime || estimateReadingTime(post.body || post.excerpt);

                  return (
                    <article
                      key={post.title}
                      className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition hover:-translate-y-1.5 hover:shadow-lg"
                    >
                      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                        {image ? (
                          <img
                            src={image}
                            alt={post.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-tr from-primary/15 to-card">
                            <BookOpen className="size-10 text-primary/30" />
                          </div>
                        )}
                        {post.category && (
                          <span className="absolute left-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary shadow backdrop-blur">
                            {post.category}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col p-6">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {post.date && (
                            <span className="inline-flex items-center gap-1 font-semibold">
                              <CalendarDays className="size-3.5" />
                              {post.date}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 font-semibold">
                            <Clock className="size-3.5" />
                            {readTime}
                          </span>
                        </div>

                        <h2 className="mt-3 text-lg font-extrabold leading-snug text-foreground transition group-hover:text-primary">
                          {post.title}
                        </h2>
                        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                          {post.excerpt}
                        </p>

                        <div className="mt-5 border-t border-border/60 pt-4">
                          <Link
                            to="/blog/$slug"
                            params={{ slug: postSlug(post) }}
                            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-primary transition group-hover:underline"
                          >
                            Ler artigo
                            <ArrowUpRight className="size-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Paginação da Listagem do Blog */}
              {totalPages > 1 && (
                <div className="mt-14 flex items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={validPage === 1}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 400, behavior: "smooth" });
                    }}
                  >
                    <ChevronLeft className="mr-1 size-4" /> Anterior
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <Button
                        key={i}
                        type="button"
                        variant={validPage === i + 1 ? "default" : "outline"}
                        className="size-10 rounded-full text-xs font-bold"
                        onClick={() => {
                          setCurrentPage(i + 1);
                          window.scrollTo({ top: 400, behavior: "smooth" });
                        }}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={validPage === totalPages}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 400, behavior: "smooth" });
                    }}
                  >
                    Próxima <ChevronRight className="ml-1 size-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-border bg-card/50 py-16 text-center">
              <BookOpen className="mx-auto size-12 text-muted-foreground/40" />
              <h3 className="mt-4 text-lg font-bold text-foreground">
                Nenhum artigo encontrado
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {search
                  ? `Não encontramos resultados para "${search}".`
                  : "Nenhum artigo cadastrado nesta categoria no momento."}
              </p>
              {(search || selectedCategory !== "Todas") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setSelectedCategory("Todas");
                    setCurrentPage(1);
                  }}
                  className="mt-4 inline-flex items-center rounded-full bg-secondary px-4 py-2 text-xs font-bold text-foreground hover:bg-secondary/80"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
